import { useEffect, useState } from 'react'
import { DateRange } from 'react-date-range'
import Button from '@mui/material/Button'
import Select from '@mui/material/Select'
import Container from '@mui/material/Container'
import Paper from '@mui/material/Paper'
import Card from '@mui/material/Card'
import MenuItem from '@mui/material/MenuItem'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import {
  ThemeProvider,
  createTheme,
  responsiveFontSizes,
} from '@mui/material/styles'

import festivalList from './festival_list.json'
import { frCH } from 'date-fns/locale'
import 'react-date-range/dist/styles.css' // main style file
import 'react-date-range/dist/theme/default.css' // theme css file
import './List.scss'
import { ContentPasteSearchOutlined } from '@mui/icons-material'

const theme = createTheme({
  typography: {
    // htmlFontSize: 12,
    fontSize: 22,
    fontFamily: 'IBM Plex Sans',
  },
})

const List = ({
  handleShow,
}: {
  handleShow: (lat: number, lng: number) => void
}) => {
  const minDate = new Date(2022, 5, 1)
  const maxDate = new Date(2022, 8, 16)
  const selectionRange = {
    startDate: minDate,
    endDate: maxDate,
    key: 'selection',
  }
  const countStep = 4
  const [currentRange, setCurrentRange] = useState([
    { startDate: new Date(), endDate: minDate, key: 'selection' },
  ])
  const [showCalendar, setShowCalendar] = useState(false)
  const [showCount, setShowCount] = useState(countStep)
  const [index, setIndex] = useState(0)
  const [startDate, setStartDate] = useState(selectionRange.startDate)
  const [endDate, setEndDate] = useState(selectionRange.endDate)
  const [styleFilter, setStyleFilter] = useState('all')
  const [starFilter, setStarFilter] = useState('Toutes les têtes d’affiche')
  const [filteredList, setFilteredList] = useState(festivalList)
  const [starList, setStarList] = useState<string[]>([])

  useEffect(() => {
    let stars: string[] = []
    festivalList.forEach((row) => {
      if (row.topstars !== '') {
        stars.push(...row.topstars.split(', '))
      }
    })
    stars.sort()
    stars.splice(0, 0, 'Toutes les têtes d’affiche')
    setStarList([...new Set(stars)])
  }, [])
  useEffect(() => {
    setFilteredList(
      festivalList.filter((item) => {
        if (
          starFilter !== 'Toutes les têtes d’affiche' &&
          styleFilter !== 'all'
        ) {
          return (
            new Date(item.start_date) <= endDate &&
            new Date(item.end_date) >= startDate &&
            item.style_category === styleFilter &&
            item.topstars.search(starFilter) >= 0
          )
        } else if (starFilter !== 'Toutes les têtes d’affiche') {
          return (
            new Date(item.start_date) <= endDate &&
            new Date(item.end_date) >= startDate &&
            item.topstars.search(starFilter) >= 0
          )
        } else if (styleFilter !== 'all') {
          return (
            new Date(item.start_date) <= endDate &&
            new Date(item.end_date) >= startDate &&
            item.style_category === styleFilter
          )
        } else {
          return (
            new Date(item.start_date) <= endDate &&
            new Date(item.end_date) >= startDate
          )
        }
      })
    )
  }, [showCount, endDate, startDate, styleFilter, starFilter])

  const handleInputClick = () => {
    setShowCalendar(!showCalendar)
  }

  const handleSelect = (ranges: any) => {
    setCurrentRange([ranges.selection])
    setStartDate(ranges.selection.startDate)
    setEndDate(ranges.selection.endDate)
    setIndex(0)
    if (ranges.selection.startDate !== ranges.selection.endDate) {
      setShowCalendar(false)
    }
  }
  const handleStarChange = (event: any) => {
    if (event.target.innerText !== undefined) {
      setStarFilter(event.target.innerText)
    } else {
      setStarFilter('Toutes les têtes d’affiche')
    }
  }
  const handleStyleChange = (event: any) => {
    const newStyleFilter = event.target.value
    setStyleFilter(newStyleFilter)
    setIndex(0)
  }
  const resetFilters = () => {
    setStartDate(minDate)
    setEndDate(maxDate)
    setStyleFilter('all')
    setStarFilter('Toutes les têtes d’affiche')
    setIndex(0)
  }
  return (
    <>
      <Container sx={{padding: '2px !important'}}>
        <Paper elevation={2} sx={{ marginBottom: '1em' }}>
          <div className='filters-container'>
            <ThemeProvider theme={theme}>
              <TextField
                className='date-range-display ui input text'
                type='text'
                sx={{ width: '100%' }}
                value={
                  'Du ' +
                  startDate.toLocaleDateString() +
                  ' au ' +
                  endDate.toLocaleDateString()
                }
                onClick={handleInputClick}
              />

              <Autocomplete
                disablePortal
                options={starList}
                value={starFilter}
                sx={{ marginTop: '1rem', marginBottom: '1rem' }}
                renderInput={(params) => (
                  <TextField {...params} label='Têtes d’affiche' />
                )}
                onChange={handleStarChange}
              />

              <Select
                value={styleFilter}
                sx={{ width: '100%' }}
                onChange={handleStyleChange}
              >
                <MenuItem value='all'>Tous les styles</MenuItem>
                <MenuItem value='rock/pop/rap'>Rock / pop / rap / ...</MenuItem>
                <MenuItem value='classique'>Classique</MenuItem>
                <MenuItem value='jazz'>Jazz</MenuItem>
                <MenuItem value='tous styles'>Autres styles</MenuItem>
              </Select>
            </ThemeProvider>
          </div>
        </Paper>

        <Paper elevation={2}>
          <div className='list-container'>
            <div className='pagination-buttons-container'>
              {filteredList.length > countStep && (
                <>
                  <Button
                    variant='contained'
                    onClick={() => {
                      setIndex(index - countStep)
                    }}
                    disabled={index >= showCount ? false : true}
                  >
                    <ChevronLeftIcon />
                    <span className='mobile hidden'>Précédent</span>
                  </Button>
                  <Button
                    variant='contained'
                    onClick={() => {
                      setIndex(index + countStep)
                    }}
                    disabled={
                      index + showCount < filteredList.length ? false : true
                    }
                  >
                    <span className='mobile hidden'>Suivant</span>
                    <ChevronRightIcon />
                  </Button>
                </>
              )}
            </div>

            {showCalendar && (
              <div>
                {/* <i className='close icon'></i> */}
                <DateRange
                  locale={frCH}
                  // minDate={minDate}
                  // maxDate={maxDate}
                  shownDate={minDate}
                  color={'#2c3890'}
                  dateDisplayFormat='d MMM yyyy'
                  editableDateInputs={true}
                  moveRangeOnFirstSelection={false}
                  ranges={currentRange}
                  onChange={handleSelect}
                />
              </div>
            )}

            <div>
              {filteredList.slice(index, index + countStep).length === 0 && (
                <div className='no-result'>
                  <p>Aucun festival n’a été trouvé pour ces critères.</p>
                  <Button variant='contained' onClick={resetFilters}>
                    Réintialiser les filtres
                  </Button>
                </div>
              )}
              {filteredList.slice(index, index + countStep).map((item) => {
                return (
                  <div className={`list-item ${item.style_css}`} key={item.id}>
                    <div className='name ui header'>{item.name}</div>
                    {/* {item.topstars && <div>{item.topstars_label}: {item.topstars}</div> } */}
                    <div className='body'>
                      <div className='date inline'>
                        {item.dates} – {item.style}
                      </div>
                      {item.topstars && (
                        <div className='inline'>
                          {item.topstars_label}: {item.topstars}
                        </div>
                      )}
                      <div className='location inline'>{item.location}</div>
                    </div>
                    <div className='footer'>
                      <Button
                        onClick={() =>
                          handleShow(parseFloat(item.lat), parseFloat(item.lng))
                        }
                      >
                        <i className='map outline icon'></i>Voir sur la carte
                      </Button>
                      <Button
                        href={item.website}
                        target='_blank'
                        rel='noreferrer'
                      >
                        <i className='external alternate icon'></i>
                        {item.website.includes('facebook.com')
                          ? 'Page Facebook'
                          : 'Site Web'}
                      </Button>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </Paper>
      </Container>
    </>
  )
}

export default List
